import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import {
  UNITS,
  isStorableBarcode,
  isValidGtin,
  isValidSku,
} from '@aeris/catalog'
import {
  PRODUCT_CONDITION_VALUES,
  UNIT_VALUES,
  type ProductSummary,
} from '@aeris/contracts'
import { ZERO_QUANTITY, formatQuantity, parseQuantity } from '@aeris/inventory'
import {
  PERCENT_SCALE,
  UNIT_PRICE_SCALE,
  abs,
  formatMoney,
  isNegative,
  parseHumanAmount,
  toString as decimalToString,
} from '@aeris/money'
import { marginFromPrice, priceFromMargin, profitOf } from '@aeris/pricing'
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  RadioGroup,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  createProduct,
  fetchNextProductCodes,
  fetchProduct,
  updateProduct,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

import { BrandField } from './brand-field.tsx'
import { CategoryField } from './category-field.tsx'
import {
  PriceHistoryDialog,
  PriceHistoryTable,
} from './price-history-dialog.tsx'
import { ProductApplications } from './product-applications.tsx'
import { ProductAttributes } from './product-attributes.tsx'
import { ProductColoursPanel } from './product-colours-panel.tsx'
import { ProductGallery } from './product-gallery.tsx'
import { ProductIdentity } from './product-identity.tsx'
import { ProductStockPanel } from './product-stock-panel.tsx'
import { ProvenanceChip } from './provenance-chip.tsx'

/**
 * Create and edit a product.
 *
 * The client validates the same barcode check digit the server does, because a
 * round trip to be told a digit is wrong is a bad experience at a counter. It
 * is not *instead of* the server check — `@aeris/catalog` is the one package
 * both sides import, so the two answers cannot drift, and the server checks
 * again regardless.
 *
 * Não há campo de ESTOQUE: saldo é do módulo de estoque, e a Master Spec
 * (seção 12) proíbe guardá-lo no produto.
 *
 * Há campo de custo e de preço desde a F123 — e isso NÃO contraria a seção 12,
 * que diz "não guardar preço diretamente no Product como *única fonte de
 * verdade*". O custo vai para a variante e o preço para a lista padrão; os dois
 * módulos continuam sendo a fonte. Uma versão anterior deste comentário lia a
 * regra como "proíbe preço no produto", que é mais forte do que o que está
 * escrito.
 */

/** Centímetros, com vírgula ou ponto. Em branco é "não medido". */
const dimensionField = z
  .string()
  .trim()
  .refine(
    (value) => value.length === 0 || /^\d{1,4}([.,]\d{1,2})?$/.test(value),
    'Use centímetros, com até duas casas — ex.: 22,5'
  )

const schema = z
  .object({
    name: z.string().trim().min(1, 'Informe o nome').max(200),
    /*
     * Em branco é permitido: no cadastro o servidor dá o próximo código de
     * seis dígitos da loja (F128). Na edição, em branco não é enviado e o
     * código fica como está — um produto em etiqueta não perde o código.
     */
    sku: z
      .string()
      .trim()
      .max(40)
      .refine(
        (value) => value.length === 0 || isValidSku(value),
        'Use letras, números, ponto, hífen, barra ou sublinhado'
      ),
    /*
     * A regra é a do banco, e não a da GS1 — F168.
     *
     * As etiquetas desta loja trazem o número do sistema antigo, sem dígito
     * verificador. Exigir GTIN aqui recusava produto que a própria importação
     * tinha acabado de gravar: *"os que têm 0 no final não dá pra salvar"*, que
     * é como o gerente descobriu. O aviso de que o número não é de fábrica está
     * embaixo do campo, e é um aviso — não uma recusa.
     */
    gtin: z
      .string()
      .trim()
      .refine(
        (value) => value.length === 0 || isStorableBarcode(value),
        'Só números, no máximo 20, e não pode ser só zeros'
      ),
    unit: z.enum(UNIT_VALUES),
    ncm: z
      .string()
      .trim()
      .refine(
        (value) =>
          value.length === 0 || /^[0-9]{8}$/.test(value.replace(/\D/g, '')),
        'O NCM tem 8 dígitos'
      ),
    brand: z.string().trim().max(120),
    // F163: o id da categoria, ou vazio para "sem categoria".
    categoryId: z.string(),
    // F141: texto livre, os dois.
    supplierReference: z.string().trim().max(80, 'Até 80 caracteres'),
    location: z.string().trim().max(80, 'Até 80 caracteres'),
    tracksStock: z.boolean(),
    /*
     * O estoque mínimo (F134). Vazio é "sem mínimo", e é diferente de zero —
     * sem mínimo o produto nunca aparece em "Abaixo do mínimo".
     */
    minimumStock: z
      .string()
      .trim()
      .refine(
        (value) =>
          value.length === 0 ||
          (parseHumanAmount(value, 6) !== null && !value.includes('-')),
        'Use um número como 5'
      ),
    description: z.string().trim().max(2000),

    /*
     * Custo e preço (F123).
     *
     * Vazio é "não mexa", e é diferente de zero — a rota só envia o que foi
     * preenchido. Aceita vírgula porque num balcão brasileiro ninguém digita
     * ponto decimal; `parseHumanAmount` entende as duas.
     */
    cost: z
      .string()
      .trim()
      .refine(
        (value) => value.length === 0 || parseHumanAmount(value, 6) !== null,
        'Use um valor como 19,90'
      ),
    salePrice: z
      .string()
      .trim()
      .refine(
        (value) => value.length === 0 || parseHumanAmount(value, 6) !== null,
        'Use um valor como 19,90'
      ),

    /*
     * A ficha de anúncio (F108).
     *
     * Vive no mesmo formulário e no mesmo Salvar porque são campos escalares do
     * mesmo cadastro, como o NCM. Peso e caixa não são "coisas de marketplace":
     * são a resposta para "quanto custa mandar isto pelo correio?", que a loja
     * responde no balcão todo dia.
     */
    condition: z.enum(PRODUCT_CONDITION_VALUES),
    warrantyMonths: z
      .string()
      .trim()
      .refine(
        (value) =>
          value.length === 0 ||
          (/^\d{1,3}$/.test(value) && Number(value) <= 120),
        'Garantia em meses, de 0 a 120'
      ),
    warrantyNote: z.string().trim().max(200),
    listingDescription: z.string().trim().max(50000),
    weightGrams: z
      .string()
      .trim()
      .refine(
        (value) =>
          value.length === 0 || (/^\d{1,7}$/.test(value) && Number(value) > 0),
        'Peso em gramas, maior que zero'
      ),
    lengthCm: dimensionField,
    widthCm: dimensionField,
    heightCm: dimensionField,

    /*
     * F133 — só no CADASTRO. `openingQuantity` em branco é "não informei" (o
     * produto nasce como antes); "0" é "não tenho nenhum".
     */
    stockMode: z.enum(['now', 'later']),
    openingQuantity: z
      .string()
      .trim()
      .refine(
        (value) =>
          value.length === 0 ||
          (parseHumanAmount(value, 6) !== null && !value.includes('-')),
        'Use uma quantidade como 5 ou 2,5'
      ),
  })
  /*
   * As três medidas, ou nenhuma.
   *
   * O CHECK `product_variant_package_all_or_none` (migração 0056) já recusa o
   * meio-termo, e o servidor devolve 400. Dizer aqui é o que evita a pessoa
   * descobrir isso depois de clicar em Salvar, com o diálogo inteiro
   * preenchido.
   */
  .superRefine((values, ctx) => {
    const filled = [values.lengthCm, values.widthCm, values.heightCm].filter(
      (value) => value.length > 0
    ).length

    if (filled === 0 || filled === 3) return

    for (const field of ['lengthCm', 'widthCm', 'heightCm'] as const) {
      if (values[field].length > 0) continue
      ctx.addIssue({
        code: 'custom',
        path: [field],
        message: 'Preencha as três medidas, ou deixe as três em branco',
      })
    }
  })

type FormValues = z.infer<typeof schema>

/**
 * `tracksStock` vem do padrão da empresa (F070).
 *
 * Antes era `true` fixo, com o comentário "o servidor herda o padrão da
 * empresa; o formulário tem de abrir em ALGUMA coisa". Era verdade e era
 * inofensivo enquanto ninguém podia mudar o padrão — a coluna existia sem
 * tela. Deixou de ser as duas coisas na hora em que a tela de Empresa passou a
 * escrever nela: o formulário manda `tracksStock` SEMPRE, então o `??
 * companyDefault()` do servidor nunca era alcançado por esta tela, e a loja
 * que acabou de dizer "não conto estoque" veria cada produto novo nascer
 * contando. O ajuste da empresa valeria para a API e não para quem cadastra.
 */
const emptyValues = (tracksStock: boolean): FormValues => ({
  name: '',
  /*
   * Em branco só até o servidor responder: o formulário abre com o próximo
   * código da loja (`000123`, F128) e o código de barras interno já no campo
   * (F133), editáveis. Quem usa o código do fornecedor apaga e digita o dele.
   *
   * Só na CRIAÇÃO: `valuesFrom` traz o código que o produto já tem, e gerar um
   * na edição trocaria o código de um produto que já está em etiqueta, em
   * anúncio e em nota.
   */
  sku: '',
  stockMode: 'now',
  openingQuantity: '',
  gtin: '',
  unit: 'UN',
  ncm: '',
  brand: '',
  categoryId: '',
  supplierReference: '',
  location: '',
  tracksStock,
  minimumStock: '',
  description: '',
  condition: 'new',
  warrantyMonths: '',
  warrantyNote: '',
  listingDescription: '',
  weightGrams: '',
  lengthCm: '',
  widthCm: '',
  heightCm: '',
  cost: '',
  salePrice: '',
})

const valuesFrom = (product: ProductSummary): FormValues => {
  const variant = product.variants.find((entry) => entry.isDefault)
  return {
    name: product.name,
    sku: variant?.sku ?? '',
    stockMode: 'now',
    openingQuantity: '',
    gtin: variant?.gtin ?? '',
    unit: variant?.unit ?? 'UN',
    ncm: variant?.ncm ?? '',
    brand: product.brand ?? '',
    categoryId: product.categoryId ?? '',
    supplierReference: variant?.supplierReference ?? '',
    // A desta loja: é a que a lista trouxe, para a filial da sessão.
    location: product.location ?? '',
    tracksStock: product.tracksStock,
    minimumStock: forQuantity(product.minimumStock),
    description: product.description ?? '',
    condition: product.condition,
    warrantyMonths:
      product.warrantyMonths === null ? '' : String(product.warrantyMonths),
    warrantyNote: product.warrantyNote ?? '',
    listingDescription: product.listingDescription ?? '',
    weightGrams:
      product.weightGrams === null ? '' : String(product.weightGrams),
    lengthCm: product.dimensions?.lengthCm ?? '',
    widthCm: product.dimensions?.widthCm ?? '',
    heightCm: product.dimensions?.heightCm ?? '',
    /*
     * Preenchidos com o que está salvo (F125).
     *
     * A F123 abria os dois em branco para ninguém reescrever o custo médio sem
     * perceber. O pedido do Eduardo foi o contrário, e com razão: um campo de
     * preço vazio sobre um produto que TEM preço convida a redigitar, e a
     * redigitar errado. O risco que a F123 temia foi resolvido de outro jeito —
     * o formulário só manda o campo que a pessoa MUDOU (ver `onSubmit`), e toda
     * mudança vira linha no histórico do produto.
     */
    cost: forField(product.cost?.averageCost ?? null),
    salePrice: forField(product.price?.basePrice ?? null),
  }
}

/**
 * `10.900000` vira `10,90` — o que o balcão digita e o `parseHumanAmount` lê.
 *
 * Duas casas no mínimo e sem zeros sobrando: o custo médio pode ter seis casas
 * de verdade (10,333333), e essas ficam, porque arredondar aqui seria mostrar
 * um custo que não é o gravado.
 */
function forField(value: string | null): string {
  if (value === null) return ''
  const [whole = '0', fraction = ''] = value.split('.')
  const trimmed = fraction.replace(/0+$/, '').padEnd(2, '0')
  return `${whole},${trimmed}`
}

/**
 * `5.000000` vira `5`, e `2.500000` vira `2,5`: o mínimo como o balcão digita.
 */
function forQuantity(value: string | null): string {
  if (value === null) return ''
  const [whole = '0', fraction = ''] = value.split('.')
  const trimmed = fraction.replace(/0+$/, '')
  return trimmed.length === 0 ? whole : `${whole},${trimmed}`
}

/** Quais campos moram na aba "Anúncio" — ver `onSubmit`. */
const LISTING_FIELDS = new Set([
  'condition',
  'warrantyMonths',
  'warrantyNote',
  'listingDescription',
  'weightGrams',
  'lengthCm',
  'widthCm',
  'heightCm',
])

/**
 * O bloco de dinheiro do cadastro (F123).
 *
 * **Markup sobre o custo**, e a tela diz isso com todas as letras. "30% de
 * margem" vale R$ 130,00 sobre um custo de R$ 100 neste entendimento, e
 * R$ 142,86 no outro — a diferença não é acadêmica, é a conversa com o
 * contador. A F110 já tinha escolhido markup na trava de margem mínima do
 * canal, e duas definições no mesmo ERP dariam duas telas certas discordando
 * sobre o mesmo produto.
 *
 * O cálculo anda para os DOIS lados: quem tem custo e quer margem digita os
 * dois e lê o preço; quem já sabe o preço digita o preço e lê a margem que ele
 * representa. Nenhum dos dois é a fonte de verdade — a fonte é o que for
 * salvo, e o que se salva é custo e preço.
 *
 * **Na EDIÇÃO o custo não aparece, e com ele some a margem.** O custo médio é
 * calculado pelas compras (ADR-0020); um campo aqui o sobrescreveria com um
 * número digitado de memória, apagando a conta de várias notas. Sem custo não
 * há margem para calcular, e inventar uma seria pior que não mostrar nenhuma.
 * O preço fica: ele mora numa lista, listas mudam, e mudar é o ponto.
 */
const PriceBlock = ({
  cost,
  salePrice,
  margin,
  onMargin,
  costField,
  priceField,
}: {
  cost: string
  salePrice: string
  margin: string
  onMargin: (value: string) => void
  /** Nulo esconde custo e margem. Desde a F125 a ficha sempre o mostra. */
  costField: ReactNode | null
  priceField: ReactNode
}): ReactNode => {
  const costValue = parseHumanAmount(cost, UNIT_PRICE_SCALE)
  const priceValue = parseHumanAmount(salePrice, UNIT_PRICE_SCALE)

  const profit =
    costValue !== null && priceValue !== null
      ? profitOf(costValue, priceValue)
      : null

  return (
    <div className="flex flex-col gap-3 rounded-(--radius-md) border border-(--border-default) p-3">
      <div
        className={`grid gap-4 ${costField === null ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}
      >
        {costField}

        {costField === null ? null : (
          <Field label="Margem" hint="Markup sobre o custo">
            {(field) => (
              <Input
                {...field}
                value={margin}
                inputMode="decimal"
                placeholder="30"
                onChange={(event) => {
                  onMargin(event.target.value)
                }}
              />
            )}
          </Field>
        )}

        {priceField}
      </div>

      {/*
        O resultado em dinheiro, por extenso. A margem em porcentagem é fácil
        de ler errado; "sobra R$ 30,00" não é.
      */}
      {costField === null ? null : profit === null ? (
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Preencha custo e preço para ver quanto sobra. Os dois são opcionais —
          o preço vai para a lista padrão e o custo é o de abertura da peça.
        </p>
      ) : (
        <p
          className={`text-(length:--text-xs) ${
            isNegative(profit) ? 'text-(--danger)' : 'text-(--text-secondary)'
          }`}
        >
          {isNegative(profit)
            ? `Atenção: o preço está ${formatMoney(abs(profit), { symbol: true })} ABAIXO do custo.`
            : `Sobra ${formatMoney(profit, { symbol: true })} por unidade.`}
        </p>
      )}
    </div>
  )
}

/** Um campo de dinheiro na forma que o contrato espera, ou nada. */
const moneyPayload = (
  key: 'cost' | 'salePrice',
  raw: string
): Record<string, string> => {
  const parsed = parseHumanAmount(raw, UNIT_PRICE_SCALE)
  return parsed === null ? {} : { [key]: decimalToString(parsed) }
}

const factDate = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })

/**
 * O que está salvo e não se edita aqui (F125) — o pedido do Eduardo.
 *
 * "As informações que foram salvas sempre têm que estar preenchidas: a
 * quantidade, as datas." Quantidade não é campo do produto (seção 12: saldo é
 * da filial, num livro-razão), então aparece como FATO, com o nome da filial,
 * e não como campo — um campo de estoque aqui seria uma segunda porta para
 * mudar saldo sem movimento, que é exatamente o que o estoque existe para
 * impedir.
 */
const ProductFacts = ({
  product,
  onShowHistory,
}: {
  product: ProductSummary
  onShowHistory: () => void
}): ReactNode => {
  const unit =
    product.variants.find((variant) => variant.isDefault)?.unit ?? 'UN'
  const lastPurchase = product.cost?.lastPurchaseAt ?? null

  const facts: { label: string; value: string }[] = [
    ...(product.stock == null
      ? []
      : [
          {
            label: 'Em estoque',
            value: product.tracksStock
              ? `${formatQuantity(parseQuantity(product.stock.onHand) ?? ZERO_QUANTITY)} ${unit}`
              : 'não controla',
          },
        ]),
    {
      label: 'Cadastrado em',
      value: factDate.format(new Date(product.createdAt)),
    },
    {
      label: 'Alterado em',
      value: factDate.format(new Date(product.updatedAt)),
    },
    ...(product.cost === undefined
      ? []
      : [
          {
            label: 'Última compra',
            value:
              lastPurchase === null
                ? 'nenhuma'
                : factDate.format(new Date(lastPurchase)),
          },
        ]),
  ]

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) bg-(--surface-sunken) px-3 py-2">
      <dl className="flex flex-wrap gap-x-6 gap-y-1">
        {facts.map((fact) => (
          <div key={fact.label} className="flex flex-col">
            <dt className="text-(length:--text-xs) text-(--text-muted)">
              {fact.label}
            </dt>
            <dd className="text-(length:--text-sm) text-(--text-primary)">
              {fact.value}
            </dd>
          </div>
        ))}
      </dl>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onShowHistory}
        leadingIcon={<History aria-hidden="true" className="h-4 w-4" />}
      >
        Histórico de preço
      </Button>
    </div>
  )
}

export type ProductFormProps = {
  /** Absent means "create". */
  product?: ProductSummary | undefined
  /**
   * Se produto novo desta loja nasce controlando estoque (F070).
   *
   * Prop e não consulta aqui dentro: quem chama já tem a resposta na mão
   * quando abre o diálogo, e buscá-la no mount faria a caixa aparecer marcada
   * e desmarcar sozinha um instante depois — que é pior do que estar errada,
   * porque a pessoa vê a tela mudar de ideia debaixo do cursor.
   */
  defaultTracksStock?: boolean | undefined
  /**
   * Por que a caixa de estoque está desabilitada, quando está (F102).
   *
   * A empresa não controla estoque, e a razão vem pronta de quem chama —
   * `stockDisabledBecause`, que nomeia a empresa. Nula é o caso normal.
   *
   * Existe para o cadastro não MENTIR: era a objeção com que a F070 recusou o
   * interruptor mestre, e é ela que este parâmetro responde.
   */
  stockDisabledBecause?: string | null | undefined
  onClose: () => void
}

/**
 * Mounted only while the dialog is open, and keyed by the product being
 * edited. That is what makes the initial values correct without an effect
 * resetting them after the fact — see the call site in routes/produtos.tsx.
 */
export const ProductForm = ({
  product,
  defaultTracksStock = true,
  stockDisabledBecause = null,
  onClose,
}: ProductFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const { allows, session } = useAuth()
  const branchName = session?.context?.branchName ?? null
  const [formError, setFormError] = useState<string | null>(null)
  const [tab, setTab] = useState('geral')
  const editing = product !== undefined

  /*
   * Fechar com coisa digitada pergunta antes (F248). Esc, o X, o clique fora e
   * o Cancelar fechavam calados, e o que a pessoa digitou sumia. As abas que
   * gravam na hora (fotos, variações, estoque) não contam: já foram salvas.
   */
  const [discarding, setDiscarding] = useState(false)

  /*
   * A versão com que o Editar salva (F248). Abre com a da lista; um movimento
   * ou o "Liberar" na aba Estoque muda o produto (tira o "aguardando estoque"),
   * e com a versão velha o Salvar dava "alterado por outra pessoa" — sendo a
   * mesma pessoa, na mesma tela.
   */
  const [version, setVersion] = useState(product?.version ?? 0)
  const refreshVersion = (): void => {
    if (product === undefined) return
    void fetchProduct(product.id)
      .then((fresh) => {
        setVersion(fresh.product.version)
      })
      .catch(() => {
        // Sem a versão nova, o servidor ainda protege: recusa e explica.
      })
  }

  /*
   * A margem NÃO é campo do formulário (F123).
   *
   * Ela não é enviada nem gravada: o que se salva é custo e preço. Mantê-la
   * fora do `useForm` deixa isso estrutural em vez de convencional — não há
   * como ela vazar para o corpo da requisição por descuido de quem editar
   * `onSubmit` depois.
   */
  const [margin, setMargin] = useState(() => {
    if (product === undefined) return ''
    const saved = valuesFrom(product)
    const costValue = parseHumanAmount(saved.cost, UNIT_PRICE_SCALE)
    const priceValue = parseHumanAmount(saved.salePrice, UNIT_PRICE_SCALE)
    if (costValue === null || priceValue === null) return ''
    const computed = marginFromPrice(costValue, priceValue)
    return computed === null ? '' : decimalToString(computed)
  })
  const [showingHistory, setShowingHistory] = useState(false)

  // O que o formulário abriu. `onSubmit` compara com isto para mandar só o
  // dinheiro que mudou — ver o comentário lá.
  const [initial] = useState(() =>
    product === undefined
      ? emptyValues(defaultTracksStock)
      : valuesFrom(product)
  )

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getFieldState,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initial,
  })

  /*
   * `useWatch`, nunca `watch()`: o segundo devolve uma função que o React
   * Compiler não consegue memoizar, e ele desistiria de otimizar este
   * componente inteiro em silêncio. A casa já tinha essa regra escrita em
   * `member-form.tsx` e em mais quatro arquivos; eu a quebrei na primeira
   * versão disto e o lint a cobrou.
   */
  const brand = useWatch({ control, name: 'brand' })
  /** F168: só para dizer, embaixo do campo, que o número não é de fábrica. */
  const gtinTyped = useWatch({ control, name: 'gtin' })
  const categoryId = useWatch({ control, name: 'categoryId' })
  const cost = useWatch({ control, name: 'cost' })
  const salePrice = useWatch({ control, name: 'salePrice' })
  const stockMode = useWatch({ control, name: 'stockMode' })
  const tracksStock = useWatch({ control, name: 'tracksStock' })

  /*
   * O estoque inicial (F133): só no cadastro, só para quem pode lançar estoque,
   * e só quando há saldo a lançar — produto que conta, em loja que conta.
   */
  /*
   * O código e o código de barras do próximo cadastro, já no campo (F133).
   *
   * O dono, vendo "Gerado ao salvar": *"se é gerado, por que está aqui? Era pra
   * gerar e já mostrar pra editar"*. O servidor PREVÊ sem gastar número — abrir
   * e cancelar não deixa buraco. Sem `staleTime`: cada abertura pergunta de
   * novo, porque o número muda a cada cadastro.
   */
  const nextCodes = useQuery({
    queryKey: ['products', 'next-codes'],
    queryFn: ({ signal }) => fetchNextProductCodes(signal),
    enabled: !editing,
    staleTime: 0,
    gcTime: 0,
  })

  /*
   * Um efeito, e só para preencher: quando a previsão chega, ela entra no campo
   * que a pessoa ainda não tocou. Campo já digitado (`isDirty`) fica como está —
   * a resposta do servidor não pode apagar o que alguém escreveu enquanto ela
   * viajava.
   */
  useEffect(() => {
    const codes = nextCodes.data
    if (codes === undefined) return
    if (!getFieldState('sku').isDirty) setValue('sku', codes.sku)
    if (!getFieldState('gtin').isDirty) setValue('gtin', codes.gtin)
  }, [nextCodes.data, getFieldState, setValue])

  const offersOpeningStock =
    !editing &&
    tracksStock &&
    stockDisabledBecause === null &&
    allows('inventory.write')

  /*
   * O cálculo anda nos dois sentidos, e NUNCA sozinho.
   *
   * Cada lado só recalcula o outro quando a PESSOA digitou — não há efeito
   * observando os três campos. Um `useEffect` que recalculasse a partir de
   * `watch` entraria em laço (preço muda a margem, que muda o preço) ou, pior,
   * reescreveria o que ela acabou de digitar no instante em que ela parasse de
   * digitar.
   */
  const recalculatePrice = (rawMargin: string): void => {
    setMargin(rawMargin)
    const costValue = parseHumanAmount(cost, UNIT_PRICE_SCALE)
    const marginValue = parseHumanAmount(rawMargin, PERCENT_SCALE)
    if (costValue === null || marginValue === null) return
    setValue(
      'salePrice',
      decimalToString(priceFromMargin(costValue, marginValue)),
      {
        shouldValidate: true,
      }
    )
  }

  const recalculateMargin = (rawPrice: string): void => {
    setValue('salePrice', rawPrice, { shouldValidate: true })
    const costValue = parseHumanAmount(cost, UNIT_PRICE_SCALE)
    const priceValue = parseHumanAmount(rawPrice, UNIT_PRICE_SCALE)
    if (costValue === null || priceValue === null) {
      // Sem custo não há margem para mostrar — e um número inventado aqui
      // seria pior que o campo em branco.
      setMargin('')
      return
    }
    const computed = marginFromPrice(costValue, priceValue)
    setMargin(computed === null ? '' : decimalToString(computed))
  }

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      /*
       * O que o formulário mostrou é uma PREVISÃO (F133).
       *
       * Campo que a pessoa não mexeu não é enviado como valor: o servidor gera
       * de novo, dentro da trava, e dá o mesmo número — ou o seguinte, se
       * outro computador cadastrou nesse meio tempo. Nunca um repetido.
       *
       * O código de barras só é regerado quando o CÓDIGO também ficou como
       * veio: trocado o código, o número de barras que a pessoa viu vai como
       * está, em vez de outro derivado do código novo.
       */
      const codes = editing ? undefined : nextCodes.data
      const skuAsSuggested = values.sku === codes?.sku
      const generatingGtin =
        !editing &&
        ((codes !== undefined &&
          skuAsSuggested &&
          values.gtin === codes.gtin) ||
          // Sem previsão (o servidor não respondeu) e campo vazio: gera ao salvar.
          (codes === undefined && values.gtin.length === 0))

      // Empty means "no value", never an empty string in the database.
      const payload = {
        name: values.name,
        // Como sugerido (ou em branco): o servidor numera (F128).
        ...(values.sku.length === 0 || skuAsSuggested
          ? {}
          : { sku: values.sku }),
        gtin: generatingGtin || values.gtin.length === 0 ? null : values.gtin,
        unit: values.unit,
        ncm: values.ncm.length === 0 ? null : values.ncm,
        brand: values.brand.length === 0 ? null : values.brand,
        categoryId: values.categoryId.length === 0 ? null : values.categoryId,
        supplierReference:
          values.supplierReference.length === 0
            ? null
            : values.supplierReference,
        /*
         * F141: a localização é DESTA loja. Na edição só vai quando mudou —
         * abrir e salvar a ficha não regrava a gaveta de ninguém.
         */
        ...(editing && values.location === initial.location
          ? {}
          : {
              location: values.location.length === 0 ? null : values.location,
            }),
        tracksStock: values.tracksStock,
        description:
          values.description.length === 0 ? null : values.description,
        condition: values.condition,
        warrantyMonths:
          values.warrantyMonths.length === 0
            ? null
            : Number(values.warrantyMonths),
        warrantyNote:
          values.warrantyNote.length === 0 ? null : values.warrantyNote,
        listingDescription:
          values.listingDescription.length === 0
            ? null
            : values.listingDescription,
        weightGrams:
          values.weightGrams.length === 0 ? null : Number(values.weightGrams),
        /*
         * As três, ou nulo. O schema acima já garante que não existe o
         * meio-termo, então aqui basta olhar uma para saber das três.
         */
        dimensions:
          values.lengthCm.length === 0
            ? null
            : {
                lengthCm: values.lengthCm,
                widthCm: values.widthCm,
                heightCm: values.heightCm,
              },

        /*
         * Custo e preço (F123). AUSENTES quando em branco, nunca nulos.
         *
         * O contrato não aceita `null` de propósito: apagar um preço é
         * arquivar, e arquivar tem tela própria. Mandar `null` daqui apagaria
         * preço por omissão — alguém editando a marca de um produto zeraria o
         * preço dele sem ter tocado no campo.
         *
         * A vírgula do balcão vira ponto aqui, uma vez, com o mesmo
         * `parseHumanAmount` que o campo validou. O servidor recebe sempre a
         * forma canônica.
         *
         * O CUSTO só vai na criação, e o contrato de alteração nem o aceita:
         * depois da primeira compra ele é calculado, não digitado. O campo nem
         * é desenhado na edição, mas mandá-lo daqui seria a única linha
         * necessária para que voltasse a ser mandado sem ninguém notar.
         */
        /*
         * Na EDIÇÃO só vai o campo que a pessoa mudou (F125).
         *
         * O campo abre com o valor salvo formatado para o balcão. Mandá-lo de
         * volta sem mudança pareceria inofensivo e não é: um custo médio de seis
         * casas volta com as casas que o campo mostrou, e o servidor gravaria
         * uma "correção" que ninguém fez. Comparar com o que abriu é o que faz
         * "abrir e salvar" continuar sem efeito nenhum.
         */
        /*
         * O mínimo (F134), só quando mudou na edição — o mesmo cuidado do
         * custo. Em branco é `null`: tirar o mínimo é uma decisão possível.
         */
        ...(editing && values.minimumStock === initial.minimumStock
          ? {}
          : {
              minimumStock: (() => {
                const parsed = parseHumanAmount(values.minimumStock, 6)
                return parsed === null ? null : decimalToString(parsed)
              })(),
            }),
        ...(editing && values.cost === initial.cost
          ? {}
          : moneyPayload('cost', values.cost)),
        ...(editing && values.salePrice === initial.salePrice
          ? {}
          : moneyPayload('salePrice', values.salePrice)),
      }

      const openingQuantity = parseHumanAmount(values.openingQuantity, 6)
      const openingStock = !offersOpeningStock
        ? undefined
        : values.stockMode === 'later'
          ? { mode: 'later' as const }
          : openingQuantity === null
            ? undefined
            : {
                mode: 'now' as const,
                quantity: decimalToString(openingQuantity),
              }

      return product === undefined
        ? await createProduct({
            ...payload,
            ...(generatingGtin ? { generateGtin: true } : {}),
            ...(openingStock === undefined ? {} : { openingStock }),
          })
        : await updateProduct(product.id, {
            ...payload,
            // The version this form was opened with — or the one a stock
            // movement left (F248). If someone else saved in the meantime the
            // server refuses rather than overwriting them.
            version,
          })
    },
    onSuccess: (response) => {
      /*
       * O retorno à vista (F248): o diálogo fechava calado, e o código que o
       * servidor deu ao produto novo — que pode não ser o da previsão — nunca
       * aparecia.
       */
      const saved = response.product
      const code =
        (
          saved.variants.find((variant) => variant.isDefault) ??
          saved.variants[0]
        )?.sku ?? ''
      notify.success(
        editing
          ? `Produto ${code} salvo.`
          : `Produto ${code} cadastrado: ${saved.name}.`
      )
      /*
       * Closed as soon as the server accepted, and the lists refreshed after.
       *
       * It used to await `invalidateQueries` first, which awaits the refetch of
       * every active query it touches — so the dialog stayed open, with its
       * Salvar button spinning, for as long as an unrelated list screen took to
       * reload. The person's work was finished at the 201; making them watch a
       * background refresh is the dialog reporting somebody else's latency as
       * its own.
       */
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível salvar. Tente novamente.'
      )
    },
  })

  const onSubmit = handleSubmit(
    (values) => {
      // Um envio por vez (F248): o segundo, com o primeiro andando, criaria outro.
      if (mutation.isPending) return
      setFormError(null)
      mutation.mutate(values)
    },
    /*
     * Um erro numa aba fechada é um botão Salvar que não faz nada.
     *
     * O Radix desmonta o painel inativo, então a mensagem existiria num lugar
     * que ninguém vê — e a pessoa clicaria de novo, e de novo. Aqui a tela vai
     * até o erro em vez de esperar que alguém o procure.
     */
    (invalid) => {
      const first = Object.keys(invalid)[0]
      if (first !== undefined && LISTING_FIELDS.has(first)) setTab('anuncio')
      else setTab('geral')
    }
  )

  const requestClose = (): void => {
    if (isDirty && !mutation.isPending) {
      setDiscarding(true)
      return
    }
    onClose()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
    >
      <DialogContent
        title={editing ? 'Editar produto' : 'Novo produto'}
        description={
          editing
            ? product.name
            : 'O código e o código de barras já vêm preenchidos. Pode trocar pelos seus.'
        }
        /*
         * Quem é o produto, à vista em TODAS as abas (F248): o nome só aparecia
         * na Geral, e como campo editável — em Fotos ou Estoque não havia como
         * saber de qual produto se tratava.
         */
        meta={editing ? <ProductIdentity product={product} /> : undefined}
        className="max-w-2xl"
      >
        <form
          onSubmit={(event) => {
            void onSubmit(event)
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {formError === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {formError}
            </p>
          )}

          {/*
            Quatro seções, e não um formulário só (docs/UI_UX.md, FORMS: "não
            criar um formulário gigante com todos os campos").

            Só abas cujo recurso existe — o mesmo documento proíbe telas
            futuras de mentira. Compatibilidade e Fotos aparecem apenas na
            edição, porque as duas escrevem contra um produto que já tem id.
          */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="geral">Geral</TabsTrigger>
              <TabsTrigger value="anuncio">Anúncio</TabsTrigger>
              {editing ? (
                <TabsTrigger value="compatibilidade">
                  Compatibilidade
                </TabsTrigger>
              ) : null}
              {editing ? <TabsTrigger value="fotos">Fotos</TabsTrigger> : null}
              {/* F176: as cores, cada uma com código e estoque. */}
              {editing ? (
                <TabsTrigger value="variacoes">Variações</TabsTrigger>
              ) : null}
              {/* F131: estoque e histórico de preço sem sair da ficha. */}
              {editing && allows('inventory.read') ? (
                <TabsTrigger value="estoque">Estoque</TabsTrigger>
              ) : null}
              {editing && allows('pricing.read') ? (
                <TabsTrigger value="historico">Histórico de preço</TabsTrigger>
              ) : null}
            </TabsList>

            <TabsContent value="geral" className="flex flex-col gap-4 pt-4">
              <Field label="Nome" required error={errors.name?.message}>
                {(field) => (
                  <Input
                    {...field}
                    {...register('name')}
                    autoFocus
                    placeholder="Cabo HDMI 2.1 — 2 m"
                  />
                )}
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Código interno (SKU)"
                  required={editing}
                  error={errors.sku?.message}
                  hint={
                    editing
                      ? 'Convertido para maiúsculas'
                      : nextCodes.isError
                        ? 'Não deu para gerar agora. Em branco, o Aeris numera ao salvar'
                        : 'O próximo número da loja. Pode trocar pelo seu'
                  }
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('sku')}
                      placeholder={
                        !editing && nextCodes.isPending
                          ? 'Gerando…'
                          : 'CABO-HDMI-2M'
                      }
                      className="font-(--font-mono)"
                    />
                  )}
                </Field>

                <Field label="Unidade" required error={errors.unit?.message}>
                  {(field) => (
                    <Select {...field} {...register('unit')}>
                      {UNIT_VALUES.map((code) => (
                        <option key={code} value={code}>
                          {code} — {UNITS[code].label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field
                  label="Código de barras"
                  error={errors.gtin?.message}
                  hint={
                    /*
                     * Três frases possíveis, e a terceira é a que importa: um
                     * número que não é GTIN vale aqui dentro e na etiqueta da
                     * prateleira, mas a nota fiscal vai sair com "SEM GTIN" —
                     * `cEAN` só aceita código de fábrica. Dizer isso na hora
                     * evita a descoberta no meio de uma emissão.
                     */
                    gtinTyped.trim().length > 0 && !isValidGtin(gtinTyped)
                      ? 'Não é um código de fábrica: vale aqui e na etiqueta, e a nota fiscal sairá como SEM GTIN'
                      : editing
                        ? 'EAN, UPC ou GTIN-14. Opcional'
                        : 'Gerado pela loja (começa com 2). Bipe o da embalagem para usar o de fábrica, ou apague para ficar sem'
                  }
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('gtin')}
                      inputMode="numeric"
                      placeholder={
                        !editing && nextCodes.isPending
                          ? 'Gerando…'
                          : '7894900011517'
                      }
                      className="font-(--font-mono)"
                    />
                  )}
                </Field>

                <Field
                  label="NCM"
                  error={errors.ncm?.message}
                  hint="Classificação fiscal. Opcional"
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('ncm')}
                      inputMode="numeric"
                      placeholder="8544.42.00"
                      className="font-(--font-mono)"
                    />
                  )}
                </Field>
              </div>

              {/*
                F141 — de quem é a peça e onde ela fica. A localização é desta
                loja: a mesma tela mora na gaveta 3 da Matriz e na prateleira B
                da loja nova.
              */}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Identificação do fornecedor"
                  error={errors.supplierReference?.message}
                  hint="Como a loja identifica o fornecedor da peça. Opcional"
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('supplierReference')}
                      placeholder="NN, WEFIX, MASTER…"
                    />
                  )}
                </Field>

                <Field
                  label="Localização da peça"
                  error={errors.location?.message}
                  hint={
                    branchName === null
                      ? 'Onde a peça fica na loja. Opcional'
                      : `Onde a peça fica em ${branchName}. Cada loja tem a sua`
                  }
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('location')}
                      placeholder="Gaveta 3, Prateleira B2…"
                    />
                  )}
                </Field>
              </div>

              {/*
                F123 — custo, margem e preço no cadastro.

                Aqui e não numa aba separada: quem cadastra um produto sabe
                quanto pagou e por quanto vai vender NAQUELE momento, com a
                nota do fornecedor na mão. Mandá-lo a outra tela depois é
                garantir que metade do catálogo fique sem preço.

                Nada disso vira coluna do produto: o custo é da variante e o
                preço é da lista padrão. A Master Spec (seção 12) proíbe o
                produto ser a ÚNICA fonte de verdade do preço — e não proíbe um
                campo que escreva no módulo que já existe.
              */}
              <PriceBlock
                cost={cost}
                salePrice={salePrice}
                margin={margin}
                onMargin={recalculatePrice}
                costField={
                  <Field
                    label="Custo"
                    hint={editing ? 'Custo médio atual' : undefined}
                    error={errors.cost?.message}
                  >
                    {(field) => (
                      <Input
                        {...field}
                        {...register('cost')}
                        inputMode="decimal"
                        placeholder="100,00"
                      />
                    )}
                  </Field>
                }
                priceField={
                  <Field
                    label="Preço de venda"
                    error={errors.salePrice?.message}
                  >
                    {(field) => (
                      <Input
                        {...field}
                        value={salePrice}
                        inputMode="decimal"
                        placeholder="130,00"
                        onChange={(event) => {
                          recalculateMargin(event.target.value)
                        }}
                      />
                    )}
                  </Field>
                }
              />

              {product === undefined ? null : (
                <ProductFacts
                  product={product}
                  onShowHistory={() => {
                    setShowingHistory(true)
                  }}
                />
              )}

              {/*
                F158: escolhida no cadastro, não digitada.

                O campo era um `Input` livre, e o catálogo terminou com 2,9% de
                marcas preenchidas em grafias que nenhum relatório agrupa. Quem
                precisa de uma marca nova cria daqui mesmo — mandar a pessoa a
                outra tela no meio do cadastro é como se chega àquele 2,9%.
              */}
              <Field
                label="Marca"
                hint="Escolha da lista, ou digite para criar uma nova."
                error={errors.brand?.message}
              >
                {(field) => (
                  <BrandField
                    id={field.id}
                    aria-describedby={field['aria-describedby']}
                    value={brand}
                    onValueChange={(name) => {
                      setValue('brand', name, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }}
                  />
                )}
              </Field>

              {/*
                F163: a família da peça — tela, bateria.

                Ao lado da marca porque são a mesma pergunta para quem
                cadastra ("o que é isto?"), e porque é ela que o Estoque usa
                para responder quantas baterias a loja tem e quanto isso vale.
              */}
              <Field
                label="Categoria"
                hint="Escolha da lista, ou digite para criar uma nova."
              >
                {(field) => (
                  <CategoryField
                    id={field.id}
                    aria-describedby={field['aria-describedby']}
                    value={categoryId === '' ? null : categoryId}
                    onValueChange={(next) => {
                      setValue('categoryId', next ?? '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }}
                  />
                )}
              </Field>

              {/*
            F028. Not a Select of two options and not buried in a settings
            screen: it is one fact about this product, and the shop that needs
            it needs it while registering the product.
          */}
              <label className="flex items-start gap-2 text-(length:--text-sm) text-(--text-secondary)">
                {/*
              Desabilitada quando a LOJA não conta (F102) — e o valor continua
              sendo ENVIADO.

              Nunca forçar `false` aqui: o pedido é que a decisão de cada
              produto sobreviva ao desligamento e volte a valer quando alguém
              religar a chave. O que muda é só quem pode mexer nela agora.
            */}
                <input
                  type="checkbox"
                  className="mt-1"
                  disabled={stockDisabledBecause !== null}
                  {...register('tracksStock')}
                />
                <span>
                  Controlar estoque deste produto
                  <span className="block text-(length:--text-xs) text-(--text-muted)">
                    {stockDisabledBecause ??
                      'Desmarcado, o produto não tem saldo: vender não movimenta nada e nunca é recusado por falta. Novos produtos já vêm com a resposta da loja, definida em Empresa.'}
                  </span>
                </span>
              </label>

              {tracksStock && stockDisabledBecause === null ? (
                <Field
                  label="Estoque mínimo"
                  error={errors.minimumStock?.message}
                  hint="Abaixo disso, em cada filial, o produto aparece em “Abaixo do mínimo” no painel e no Estoque. Em branco, sem mínimo"
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('minimumStock')}
                      inputMode="decimal"
                      placeholder="5"
                      className="sm:w-40"
                    />
                  )}
                </Field>
              ) : null}

              {offersOpeningStock ? (
                <section
                  aria-labelledby="product-opening-stock"
                  className="flex flex-col gap-3 rounded-(--radius-md) border border-(--border-default) p-3"
                >
                  <h3
                    id="product-opening-stock"
                    className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)"
                  >
                    Estoque inicial
                  </h3>
                  <RadioGroup
                    name="stockMode"
                    value={stockMode}
                    onValueChange={(value) => {
                      setValue('stockMode', value)
                    }}
                    options={[
                      {
                        value: 'now',
                        label: 'Informar a quantidade agora',
                      },
                      {
                        value: 'later',
                        label: 'Conferir o estoque depois',
                      },
                    ]}
                  />
                  {stockMode === 'now' ? (
                    <Field
                      label="Quantidade em estoque"
                      error={errors.openingQuantity?.message}
                      hint="Entra como entrada de estoque na filial atual. Em branco, o produto nasce sem saldo"
                    >
                      {(field) => (
                        <Input
                          {...field}
                          {...register('openingQuantity')}
                          inputMode="decimal"
                          placeholder="5"
                          className="sm:w-40"
                        />
                      )}
                    </Field>
                  ) : (
                    <InlineAlert tone="info">
                      O produto fica marcado como &quot;Aguardando estoque&quot;
                      e não vende no PDV, em vendas nem em orçamentos até alguém
                      dar entrada ou ajustar o estoque dele.
                    </InlineAlert>
                  )}
                </section>
              ) : null}

              <Field
                label="Descrição"
                error={errors.description?.message}
                hint="A frase curta, que aparece no balcão e no orçamento"
              >
                {(field) => (
                  <Textarea {...field} {...register('description')} />
                )}
              </Field>
            </TabsContent>

            {/*
              F108 — a ficha de anúncio.

              Peso e caixa não são "coisas de marketplace": são a resposta para
              "quanto custa mandar isto pelo correio?". Um marketplace apenas
              recusa publicar sem elas.
            */}
            <TabsContent value="anuncio" className="flex flex-col gap-4 pt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Peso da embalagem (g)"
                  error={errors.weightGrams?.message}
                  hint="Bruto, com a caixa. Em gramas"
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('weightGrams')}
                      inputMode="numeric"
                      placeholder="350"
                    />
                  )}
                </Field>

                <Field label="Condição" error={errors.condition?.message}>
                  {(field) => (
                    <Select {...field} {...register('condition')}>
                      <option value="new">Novo</option>
                      <option value="used">Usado</option>
                      <option value="refurbished">Recondicionado</option>
                    </Select>
                  )}
                </Field>
              </div>

              <ProvenanceChip
                provenance={product?.listingProvenance.weightGrams ?? null}
              />

              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Comprimento (cm)"
                  error={errors.lengthCm?.message}
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('lengthCm')}
                      inputMode="decimal"
                      placeholder="22,5"
                    />
                  )}
                </Field>
                <Field label="Largura (cm)" error={errors.widthCm?.message}>
                  {(field) => (
                    <Input
                      {...field}
                      {...register('widthCm')}
                      inputMode="decimal"
                      placeholder="10"
                    />
                  )}
                </Field>
                <Field label="Altura (cm)" error={errors.heightCm?.message}>
                  {(field) => (
                    <Input
                      {...field}
                      {...register('heightCm')}
                      inputMode="decimal"
                      placeholder="4"
                    />
                  )}
                </Field>
              </div>

              <ProvenanceChip
                provenance={product?.listingProvenance.dimensions ?? null}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Garantia (meses)"
                  error={errors.warrantyMonths?.message}
                  hint="Em branco é não declarada; 0 é sem garantia"
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('warrantyMonths')}
                      inputMode="numeric"
                      placeholder="3"
                    />
                  )}
                </Field>
                <Field
                  label="Observação da garantia"
                  error={errors.warrantyNote?.message}
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('warrantyNote')}
                      placeholder="Garantia do vendedor"
                    />
                  )}
                </Field>
              </div>

              <Field
                label="Descrição do anúncio"
                error={errors.listingDescription?.message}
                hint="O texto longo, separado da descrição do balcão"
              >
                {(field) => (
                  <Textarea
                    {...field}
                    {...register('listingDescription')}
                    rows={6}
                  />
                )}
              </Field>

              <ProvenanceChip
                provenance={
                  product?.listingProvenance.listingDescription ?? null
                }
              />

              <div className="flex flex-col gap-2">
                <span className="text-(length:--text-sm) font-medium text-(--text-primary)">
                  Ficha técnica
                </span>
                <p className="text-(length:--text-xs) text-(--text-muted)">
                  Voltagem, cor, material, medida. Grava na hora, sem esperar o
                  Salvar.
                </p>
                {product === undefined ? (
                  <p className="rounded-(--radius-md) border border-dashed border-(--border-default) px-3 py-2 text-(length:--text-xs) text-(--text-muted)">
                    Cadastre o produto para preencher a ficha técnica.
                  </p>
                ) : (
                  <ProductAttributes
                    productId={product.id}
                    canWrite={allows('catalog.write')}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent
              value="compatibilidade"
              className="flex flex-col gap-4 pt-4"
            >
              {/*
            F031 — what this part fits.

            Against a product that exists, for the same reason the photographs
            below are: a compatibility is a row joining two ids, and holding
            them in the browser to replay after the insert would be a second
            write path with its own failure modes to save one click.
          */}
              {product === undefined ? null : (
                <div className="flex flex-col gap-2">
                  <span className="text-(length:--text-sm) font-medium text-(--text-primary)">
                    Aplicações
                  </span>
                  <p className="text-(length:--text-xs) text-(--text-muted)">
                    Em que aparelhos ou veículos esta peça serve. É o que faz o
                    produto aparecer quando alguém busca por modelo.
                  </p>
                  <ProductApplications
                    productId={product.id}
                    canWrite={allows('catalog.write')}
                  />
                </div>
              )}
            </TabsContent>

            {/*
              Photographs are managed against a product that exists.

              Uploading before the product is saved would mean holding files in
              the browser and replaying them after the insert — a second upload
              path with its own failure modes, to save one click. The tab only
              exists on edit, for the same reason.
            */}
            <TabsContent value="fotos" className="flex flex-col gap-4 pt-4">
              {product === undefined ? null : (
                <ProductGallery
                  productId={product.id}
                  canWrite={allows('catalog.write')}
                />
              )}
            </TabsContent>

            <TabsContent value="variacoes" className="flex flex-col gap-4 pt-4">
              {product === undefined ? null : (
                <ProductColoursPanel
                  product={product}
                  canWrite={allows('catalog.write')}
                />
              )}
            </TabsContent>

            <TabsContent value="estoque" className="flex flex-col gap-4 pt-4">
              {product === undefined ? null : (
                <ProductStockPanel
                  product={{ ...product, version }}
                  stockDisabledBecause={stockDisabledBecause}
                  onProductChanged={refreshVersion}
                />
              )}
            </TabsContent>

            <TabsContent value="historico" className="flex flex-col gap-4 pt-4">
              {product === undefined ? null : (
                <PriceHistoryTable productId={product.id} />
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
            >
              {editing ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>

        {showingHistory && product !== undefined ? (
          <PriceHistoryDialog
            productId={product.id}
            productName={product.name}
            onClose={() => {
              setShowingHistory(false)
            }}
          />
        ) : null}

        <ConfirmDialog
          open={discarding}
          onOpenChange={(next) => {
            if (!next) setDiscarding(false)
          }}
          title="Descartar as alterações?"
          description={
            editing
              ? `O que você mudou em ${product.name} não foi salvo.`
              : 'O produto ainda não foi cadastrado, e o que foi digitado se perde.'
          }
          confirmLabel="Descartar"
          cancelLabel="Continuar editando"
          onConfirm={onClose}
        />
      </DialogContent>
    </Dialog>
  )
}
